import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type {
  ActiveFilter,
  FilterCategoryGroup,
  FilterContextType,
  FilterLogic,
  FilterStep,
} from "./types";

const FilterContext = createContext<FilterContextType | null>(null);

interface FilterProviderProps {
  children: ReactNode;
  categoryGroups: FilterCategoryGroup[];
}

export function FilterProvider({
  children,
  categoryGroups,
}: FilterProviderProps) {
  const [filters, setFilters] = useState<ActiveFilter[]>([]);
  const [logic, setLogic] = useState<FilterLogic>("and");
  const [step, setStepState] = useState<FilterStep>({ type: "closed" });
  const [searchQuery, setSearchQueryState] = useState("");

  const isOpen = step.type !== "closed";

  const openPopover = useCallback(() => {
    setStepState({ type: "category_picker" });
    setSearchQueryState("");
  }, []);

  const closePopover = useCallback(() => {
    setStepState({ type: "closed" });
    setSearchQueryState("");
  }, []);

  const setStep = useCallback((newStep: FilterStep) => {
    setStepState(newStep);
    setSearchQueryState("");
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    setSearchQueryState(query);
  }, []);

  const addFilter = useCallback((filter: ActiveFilter) => {
    setFilters((prev) => [...prev, filter]);
  }, []);

  const updateFilter = useCallback(
    (
      id: string,
      updates: Partial<Pick<ActiveFilter, "operator" | "valueIds">>,
    ) => {
      setFilters((prev) =>
        prev.map((f) => (f.id === id ? { ...f, ...updates } : f)),
      );
    },
    [],
  );

  const removeFilter = useCallback((id: string) => {
    setFilters((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters([]);
  }, []);

  const toggleLogic = useCallback(() => {
    setLogic((prev) => (prev === "and" ? "or" : "and"));
  }, []);

  // F key to open filter popover
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "f" || e.key === "F") {
        const target = e.target as HTMLElement;
        const isInput =
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable;
        if (isInput) return;

        e.preventDefault();
        if (isOpen) {
          closePopover();
        } else {
          openPopover();
        }
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, openPopover, closePopover]);

  const value = useMemo<FilterContextType>(
    () => ({
      filters,
      logic,
      step,
      searchQuery,
      isOpen,
      categoryGroups,
      addFilter,
      updateFilter,
      removeFilter,
      clearAllFilters,
      toggleLogic,
      setStep,
      setSearchQuery,
      openPopover,
      closePopover,
    }),
    [
      filters,
      logic,
      step,
      searchQuery,
      isOpen,
      categoryGroups,
      addFilter,
      updateFilter,
      removeFilter,
      clearAllFilters,
      toggleLogic,
      setStep,
      setSearchQuery,
      openPopover,
      closePopover,
    ],
  );

  return <FilterContext value={value}>{children}</FilterContext>;
}

export function useFilter(): FilterContextType {
  const context = use(FilterContext);
  if (!context) {
    throw new Error("useFilter must be used within a FilterProvider");
  }
  return context;
}
